/**
 * 存储命名空间常量
 * @const {string}
 */
const STORAGE_NAMESPACE = 'shasha-tv';

/**
 * 存储键名常量, 为了提高可维护性所有key都要定义到这里
 * @enum {string}
 */
export const STORAGE_KEYS = {
  LOCALE: 'locale',
  THEME: 'theme',
  AID: 'aid', // 前端生成的aid
  VGSID: 'vgsid',
  GSID: 'gsid',
  FINGERPRINT: 'fingerprint',
  DEVICE_INFO: 'device-info',
  USER_INFO: 'user-info',
  LIVE_INFO: 'live-info',
  VIDEO_INFO: 'video-info',
  CARD_INFO: 'card-info',
  ACTIVE_CONFIG: 'active-config',
  IS_FROM_LOGIN: 'is-from-login', // 登录来源状态
  SERIES_EPISODE_PROGRESS: 'series-episode-progress', // 系列剧集观看进度
  STREAM_WATCHER_HISTORY: 'stream-watcher-history', // Last 10 sessions for user_historical_average
  APP_VERSION: 'app-version', // Last booted app version (for one-time migrations)
  VIDEO_EVENT_SOURCE: 'video-event-source', // event_source for video_played Mixpanel (home_page, search_page, etc.)
} as const;

type CacheEntry = {
  string: string;
  object: any;
};

const storageCache: Record<string, CacheEntry | null> = {};
const dirtyKeys = new Set<string>();
const removedKeys = new Set<string>();
let clearAllPending = false;
let isHydrated = false;
let flushTimer: ReturnType<typeof setTimeout> | null = null;
const FLUSH_DELAY_MS = 500;

// Shallow clone keeps reads fast; dev-only freeze catches accidental nested mutations.
const shallowClone = (value: any) => {
  if (Array.isArray(value)) {
    return [...value];
  }
  if (typeof value === 'object' && value !== null) {
    return { ...value };
  }
  return value;
};

const deepFreeze = (value: any, seen = new WeakSet<object>()) => {
  if (!value || typeof value !== 'object') return value;
  if (seen.has(value)) return value;
  seen.add(value);
  Object.getOwnPropertyNames(value).forEach(key => {
    deepFreeze((value as Record<string, unknown>)[key], seen);
  });
  return Object.freeze(value);
};

const freezeInDev = (value: any) => {
  if (process.env.NODE_ENV === 'production') return value;
  return deepFreeze(value);
};

const saveToCache = (key: string, stringValue: string, objectValue: any) => {
  const cachedObject = freezeInDev(shallowClone(objectValue));
  storageCache[key] = {
    string: stringValue,
    object: cachedObject,
  };
};

const canUseLocalStorage = () => {
  try {
    return typeof localStorage !== 'undefined';
  } catch (error) {
    return false;
  }
};

const hydrateCache = () => {
  if (isHydrated || !canUseLocalStorage()) {
    isHydrated = true;
    return;
  }
  const namespacePrefix = `${STORAGE_NAMESPACE}:`;
  Object.keys(localStorage).forEach(key => {
    if (!key.startsWith(namespacePrefix)) return;
    if (storageCache[key]) return;
    const value = localStorage.getItem(key);
    if (!value) return;
    try {
      const parsed = JSON.parse(value);
      saveToCache(key, value, parsed);
    } catch (error) {
      saveToCache(key, value, value);
    }
  });
  isHydrated = true;
};

const flushToLocalStorage = () => {
  flushTimer = null;
  if (!canUseLocalStorage()) return;
  const namespacePrefix = `${STORAGE_NAMESPACE}:`;

  if (clearAllPending) {
    clearAllPending = false;
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith(namespacePrefix)) {
        localStorage.removeItem(key);
      }
    });
    dirtyKeys.clear();
    removedKeys.clear();
    return;
  }

  removedKeys.forEach(key => {
    localStorage.removeItem(key);
  });
  removedKeys.clear();

  dirtyKeys.forEach(key => {
    const cached = storageCache[key];
    if (!cached) {
      localStorage.removeItem(key);
      return;
    }
    localStorage.setItem(key, cached.string);
  });
  dirtyKeys.clear();
};

const scheduleFlush = () => {
  if (flushTimer || !canUseLocalStorage()) return;
  flushTimer = setTimeout(flushToLocalStorage, FLUSH_DELAY_MS);
};

/** Flush pending writes to localStorage immediately. Use after setStorage when other code (e.g. other chunks) must see the value. */
export const forceFlushStorage = () => {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  flushToLocalStorage();
};

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', flushToLocalStorage);
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushToLocalStorage();
    }
  });
}

/**
 * 创建带命名空间的存储键
 * @param {string} key - 原始键名
 * @return {string} 带命名空间的键名
 * @private
 */
const createKey = (key: string): string => `${STORAGE_NAMESPACE}:${key}`;

/**
 * 创建一个带命名空间的响应式localStorage
 * @template T 存储值的类型
 * @param {string} key - 存储键名
 * @param {T} defaultValue - 默认值
 * @returns {RemovableRef<T>} 响应式存储引用
 * @example
 * ```ts
 * // 基本用法，如果已经创建了一个名为 'my-key' 的存储项，则不会创建新的存储项，值也不会被覆盖
 * const storage = createStorage('my-key', 'default value');
 *
 * // 读取值
 * console.log(storage.value);
 *
 * // 更新值
 * storage.value = 'new value';
 *
 * // 在组件中使用ly
 * const count = createStorage('count', 0);
 * const increment = () => {
 *   count.value++;
 * };
 * ```
 */
export const createStorage = <T>(key: string, defaultValue: T): T | null => {
  const existingValue = getStorage<T>(key);
  if (existingValue !== null) {
    return existingValue;
  }
  setStorage(key, defaultValue);
  return defaultValue;
};

export const setStorage = <T>(key: string, value: T) => {
  hydrateCache();
  const k = createKey(key);
  const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
  if (storageCache[k] && storageCache[k]?.string === stringValue) {
    return value;
  }
  saveToCache(k, stringValue, value);
  removedKeys.delete(k);
  dirtyKeys.add(k);
  scheduleFlush();
  return value;
};
/**
 * 和 createStorage 相同，只是为了语义化清晰
 * @param key 存储键名
 * @returns 存储值
 */
export const getStorage = <T>(key: string): T | null => {
  hydrateCache();
  const k = createKey(key);
  if (storageCache[k]) {
    return shallowClone(storageCache[k]?.object) as T;
  }
  return null;
};

/**
 * Remove a storage item by key (namespaced)
 * @param {string} key Storage key
 * @example
 * removeStorage('my-key');
 */
export const removeStorage = (key: string) => {
  hydrateCache();
  const k = createKey(key);
  if (storageCache[k]) {
    storageCache[k] = null;
  }
  dirtyKeys.delete(k);
  removedKeys.add(k);
  scheduleFlush();
};

/**
 * 清除所有属于当前命名空间的存储项
 * @example
 * ```ts
 * // 清除所有 Jaco 相关的存储
 * clearAll(); // 清空所有 Jaco: 开头的存储项
 *
 * // 在用户登出时调用
 * const logout = () => {
 *   // 其他登出逻辑...
 *   clearAll(); // 清除所有存储的数据
 * };
 * ```
 */
export const clearAll = () => {
  hydrateCache();
  const keys = canUseLocalStorage() ? Object.keys(localStorage) : Object.keys(storageCache);
  const namespacePrefix = `${STORAGE_NAMESPACE}:`;

  keys.forEach(key => {
    if (key.startsWith(namespacePrefix)) {
      const storageKey = key.substring(namespacePrefix.length);
      removeStorage(storageKey);
    }
  });

  clearAllPending = true;
  flushToLocalStorage();
};

const getCurrentUserId = () => {
  const userInfo = getStorage<any>(STORAGE_KEYS.USER_INFO);
  return userInfo?.firebase_id || '';
};

/**
 * Series episode progress data structure
 */
export interface SeriesProgress {
  episodeId: string;
  episodeNumber: number;
  seasonNumber: number;
  seasonId: string;
  timestamp: number;
}

/**
 * Get series episode progress by series ID
 * @param {string} seriesId - The series ID
 * @returns {SeriesProgress | null} The progress data or null if not found
 * @example
 * const progress = getSeriesProgress('12345');
 * if (progress) {
 *   console.log(`Last watched: Season ${progress.seasonNumber}, Episode ${progress.episodeNumber}`);
 * }
 */
export const getSeriesProgress = (seriesId: string): SeriesProgress | null => {
  const userId = getCurrentUserId();
  if (!userId || !seriesId) return null;
  const allProgress = getStorage<any>(STORAGE_KEYS.SERIES_EPISODE_PROGRESS) || {};
  if (!allProgress || typeof allProgress !== 'object') return null;
  return allProgress[userId]?.[seriesId] || null;
};

/**
 * Set series episode progress by series ID
 * @param {string} seriesId - The series ID
 * @param {SeriesProgress} progressData - The progress data to store
 * @example
 * setSeriesProgress('12345', {
 *   episodeId: 'ep-001',
 *   episodeNumber: 3,
 *   seasonNumber: 1,
 *   seasonId: 'season-1',
 *   timestamp: Date.now()
 * });
 */
export const setSeriesProgress = (seriesId: string, progressData: SeriesProgress) => {
  const userId = getCurrentUserId();
  if (!userId || !seriesId) return;
  const allProgress = getStorage<any>(STORAGE_KEYS.SERIES_EPISODE_PROGRESS) || {};
  const nextProgress = { ...allProgress };
  const existingUserProgress =
    nextProgress[userId] && typeof nextProgress[userId] === 'object' ? nextProgress[userId] : {};
  const nextUserProgress = { ...existingUserProgress };
  nextProgress[userId] = nextUserProgress;
  nextUserProgress[seriesId] = progressData;
  setStorage(STORAGE_KEYS.SERIES_EPISODE_PROGRESS, nextProgress);
};

/**
 * Remove series episode progress by series ID
 * @param {string} seriesId - The series ID
 */
export const removeSeriesProgress = (seriesId: string) => {
  const userId = getCurrentUserId();
  if (!userId || !seriesId) return;
  const allProgress = getStorage<any>(STORAGE_KEYS.SERIES_EPISODE_PROGRESS) || {};
  if (!allProgress || typeof allProgress !== 'object') return;
  if (!allProgress[userId] || typeof allProgress[userId] !== 'object') return;
  const nextProgress = { ...allProgress };
  const nextUserProgress = { ...nextProgress[userId] };
  delete nextUserProgress[seriesId];
  nextProgress[userId] = nextUserProgress;
  setStorage(STORAGE_KEYS.SERIES_EPISODE_PROGRESS, nextProgress);
};
